import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, ChevronRight, BookOpen, Clock, Tag, Copy } from "lucide-react";
import api from "../lib/api";

/**
 * Prompt Library — versioned reusable prompts.
 * Reference inside worker/workflow task fields as {{prompts.<slug>}}.
 */
export default function PromptsPage() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: "", content: "", description: "", tags: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/prompts");
      const list = Array.isArray(data?.prompts) ? data.prompts : [];
      setItems(list);
    } catch (e) {
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    setSelected(id);
    const { data } = await api.get(`/prompts/${id}`);
    setDetail(data);
    const latest = (data.versions || []).slice(-1)[0] || {};
    setForm({
      name: data.name,
      content: latest.content || "",
      description: data.description || "",
      tags: (data.tags || []).join(", "),
    });
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/prompts", {
        name: "Untitled prompt",
        content: "You are a helpful assistant.",
        description: "",
        tags: [],
      });
      await load();
      openDetail(data.prompt_id);
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.patch(`/prompts/${selected}`, {
        name: form.name,
        content: form.content,
        description: form.description,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
      });
      await load();
      openDetail(selected);
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!selected) return;
    if (!window.confirm("Delete this prompt?")) return;
    await api.delete(`/prompts/${selected}`);
    setSelected(null); setDetail(null);
    await load();
  };

  return (
    <div className="p-6 md:p-8" data-testid="prompts-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BookOpen size={18} strokeWidth={2.5} className="text-amber-600" />
            <h1 className="font-heading text-2xl md:text-3xl font-bold">Prompt Library</h1>
          </div>
          <p className="text-sm text-slate-600">Reusable, versioned prompts. Reference inside any task with <code className="bg-slate-100 border border-slate-300 rounded px-1 text-xs font-mono">{"{{prompts.<slug>}}"}</code>.</p>
        </div>
        <button onClick={create} disabled={busy} className="brut-btn" data-testid="new-prompt">
          <Plus size={14} strokeWidth={2.5} /> New prompt
        </button>
      </div>

      <div className="grid md:grid-cols-[300px_1fr] gap-4">
        <div className="space-y-1.5" data-testid="prompts-list">
          {items.length === 0 ? (
            <div className="brut-card p-6 text-center text-sm text-slate-500">
              No prompts yet. Create one to reuse across every worker & workflow.
            </div>
          ) : items.map((p) => (
            <button
              key={p.prompt_id}
              onClick={() => openDetail(p.prompt_id)}
              className={`w-full text-left brut-card p-3 hover:-translate-y-0.5 transition-transform ${selected === p.prompt_id ? "ring-2 ring-amber-500" : ""}`}
              data-testid={`prompt-${p.slug}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-bold text-sm truncate">{p.name}</div>
                  <div className="text-[10px] font-mono text-slate-400 truncate">{p.slug} · v{p.version_count}</div>
                </div>
                <ChevronRight size={14} strokeWidth={2.5} className="text-slate-300 mt-0.5" />
              </div>
              {p.content_preview && <div className="text-[11px] text-slate-500 leading-snug line-clamp-2 mt-1">{p.content_preview}</div>}
              {(p.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {p.tags.slice(0, 3).map((t) => (
                    <span key={t} className="text-[9px] uppercase tracking-widest font-heading font-bold px-1.5 py-0.5 rounded-full border border-slate-300 bg-slate-50 text-slate-600">{t}</span>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>

        <div>
          {!selected || !detail ? (
            <div className="brut-card p-12 text-center text-sm text-slate-500" data-testid="prompt-empty">
              Pick a prompt from the left, or create one.
            </div>
          ) : (
            <div className="brut-card p-5 space-y-4" data-testid="prompt-detail">
              <div className="flex items-center gap-2">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       className="brut-input text-lg font-heading font-bold flex-1"
                       data-testid="prompt-name" />
                <button onClick={() => { navigator.clipboard.writeText(`{{prompts.${detail.slug}}}`); }} title="Copy reference" className="brut-btn brut-btn-outline text-xs" data-testid="copy-ref">
                  <Copy size={12} strokeWidth={2.5} /> {`{{prompts.${detail.slug}}}`}
                </button>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 block mb-1">Description</label>
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                       placeholder="What is this prompt for?"
                       className="brut-input text-sm" data-testid="prompt-desc" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 block mb-1">Content</label>
                <textarea rows={10} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                          className="brut-input text-sm font-mono" data-testid="prompt-content" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 block mb-1">Tags (comma-separated)</label>
                <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                       placeholder="legal, writing, formal"
                       className="brut-input text-sm" data-testid="prompt-tags" />
              </div>
              <div className="flex items-center justify-between pt-2 border-t-2 border-slate-100">
                <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 flex items-center gap-1.5">
                  <Clock size={11} strokeWidth={2.5} /> v{(detail.versions || []).length} · {(detail.versions || []).length} version{(detail.versions || []).length === 1 ? "" : "s"}
                </div>
                <div className="flex gap-2">
                  <button onClick={del} className="brut-btn brut-btn-outline text-xs text-red-600 border-red-500" data-testid="delete-prompt">
                    <Trash2 size={12} strokeWidth={2.5} /> Delete
                  </button>
                  <button onClick={save} disabled={busy} className="brut-btn text-xs" data-testid="save-prompt">
                    <Save size={12} strokeWidth={2.5} /> {busy ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
